import { useEffect } from "react";
import { App as AntdApp } from "antd";

import { grantDiscard } from "@/hooks/useDiscardGuard";

/**
 * "Escape closes a drawer and discards what you typed" (platform test round
 * 4), for every form drawer in the console at once.
 *
 * Round 3 built `useDiscardGuard` and wired it into two forms; the other
 * seventy drawers still closed on a stray Escape or a press on the dimmed
 * page beside them. This is the same question asked from the shell, so no
 * drawer has to opt in: once something has been typed into a form inside a
 * drawer (or a create wizard's dialog), Escape and a press outside ask
 * "Discard what you entered?" first. The X and Cancel are deliberate and
 * close as before; an untouched form closes as before; a drawer that asks for
 * itself (`useDiscardGuard`) is not asked twice.
 *
 * It works on the page, not on the components: it notes typing by the
 * browser's own `input` event, holds back the Escape key or the press before
 * the drawer sees it, and on "Discard" hands the drawer the Escape it held.
 * Choosing from a list or flipping a switch is not typing and does not count.
 */

/** A dialog that wants the same protection marks its wrapper with this. */
export const DISCARD_GUARD_ATTRIBUTE = "data-discard-guard";

const OPEN_DRAWER = ".ant-drawer.ant-drawer-open";
const GUARDED_DIALOG = `.ant-modal-wrap[${DISCARD_GUARD_ATTRIBUTE}]`;

function isShown(element: Element): boolean {
  return element.isConnected && (element as HTMLElement).style.display !== "none";
}

/** The drawer or guarded dialog an element sits in. */
function shellOf(element: Element): HTMLElement | null {
  return element.closest<HTMLElement>(`${OPEN_DRAWER}, ${GUARDED_DIALOG}`);
}

/** Something that takes Escape for itself is open on top (a list, a calendar,
 *  a confirmation): the key is its, not the form's. */
function somethingOnTop(shell: HTMLElement): boolean {
  if (
    document.querySelector(
      ".ant-select-dropdown:not(.ant-select-dropdown-hidden), " +
        ".ant-picker-dropdown:not(.ant-picker-dropdown-hidden), " +
        ".ant-dropdown:not(.ant-dropdown-hidden), .ant-popover:not(.ant-popover-hidden)",
    )
  ) {
    return true;
  }
  return [...document.querySelectorAll(".ant-modal-wrap")].some(
    (wrap) => wrap !== shell && !shell.contains(wrap) && isShown(wrap),
  );
}

function DiscardGuard() {
  const { modal } = AntdApp.useApp();

  useEffect(() => {
    const typedIn = new Set<HTMLElement>();
    let asking = false;
    let letThrough = false;

    // A closed drawer keeps its place in the page; what was typed into it
    // last time says nothing about the next time it opens.
    const forgetClosed = () => {
      for (const shell of typedIn) {
        const open = shell.matches(OPEN_DRAWER) || (shell.matches(GUARDED_DIALOG) && isShown(shell));
        if (!open) typedIn.delete(shell);
      }
    };

    const onInput = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      // A drawer's search or filter box is not a form being filled in.
      if (!target.closest("form") && !target.closest(GUARDED_DIALOG)) return;
      const shell = shellOf(target);
      if (shell) typedIn.add(shell);
    };

    const topTyped = (): HTMLElement | null => {
      forgetClosed();
      const open = [...document.querySelectorAll<HTMLElement>(`${OPEN_DRAWER}, ${GUARDED_DIALOG}`)]
        .filter(isShown)
        .at(-1);
      return open && typedIn.has(open) ? open : null;
    };

    const close = (shell: HTMLElement) => {
      typedIn.delete(shell);
      grantDiscard();
      letThrough = true;
      try {
        const from = shell.querySelector(".ant-drawer-content, .ant-modal") ?? shell;
        from.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true,
          } as KeyboardEventInit),
        );
      } finally {
        letThrough = false;
      }
    };

    const ask = (shell: HTMLElement) => {
      if (asking) return;
      asking = true;
      modal.confirm({
        title: "Discard what you entered?",
        content: "Nothing has been saved yet. Closing now loses what you filled in.",
        okText: "Discard",
        okButtonProps: { danger: true },
        cancelText: "Keep editing",
        onOk: () => {
          asking = false;
          close(shell);
        },
        onCancel: () => {
          asking = false;
        },
      });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (letThrough || event.key !== "Escape" || asking) return;
      const shell = topTyped();
      if (!shell || somethingOnTop(shell)) return;
      event.preventDefault();
      event.stopPropagation();
      ask(shell);
    };

    // A press on the dimmed page beside a drawer, or on the backdrop of a
    // guarded dialog. `click`, because that is what closes them.
    const onClick = (event: MouseEvent) => {
      if (letThrough || asking) return;
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const beside = target.classList.contains("ant-drawer-mask")
        ? target.closest<HTMLElement>(OPEN_DRAWER)
        : target.matches(GUARDED_DIALOG)
          ? target
          : null;
      if (!beside) {
        forgetClosed();
        return;
      }
      if (beside !== topTyped()) return;
      event.preventDefault();
      event.stopPropagation();
      ask(beside);
    };

    document.addEventListener("input", onInput, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("input", onInput, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("click", onClick, true);
    };
  }, [modal]);

  return null;
}

export default DiscardGuard;
